<template>
  <workflow-review-status
    v-if="shouldRenderContent"
    :item-class="itemClass"
    :item-id="itemId"
    :ledger-id="ledgerId"
    :should-render-inline="shouldRenderInline"
    :review="effectiveReview"
    :reviews="effectiveReviews"
    :tracking-prefix="'template-screen'"
    @item-review-update="handleItemReviewUpdate"
  />
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'

import { SfLog } from '@app/adapters/logger'
import WorkflowReviewStatus from '@app/application/workflows/screens/workflow-review/components/workflow-review-status.vue'
import { TemplateReview } from '@app/application/workflows/types'
import SFetch from '@app/utils/sfetch'

interface Props {
  itemClass: 'account_entry' | 'reconciliation_text' | undefined
  itemId: number
  ledgerId: number
  review?: TemplateReview | null
  reviews: Array<{
    name: string
    time: string
    outdatedAt: Date | null
  }>
  shouldRenderContent?: boolean
  shouldRenderInline?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  review: null,
  shouldRenderContent: true,
  shouldRenderInline: false,
  reviews: () => [],
})

const effectiveReviews = ref(props.reviews)
const effectiveReview = ref(props.review)

const hasAnyUpToDateReview = computed(() => {
  return (
    effectiveReviews.value &&
    effectiveReviews.value.some((review) => review.outdatedAt === null)
  )
})

const reviewers = computed(() => {
  return effectiveReviews.value.map((review) => review.name).join(', ')
})

onMounted(() => {
  window.eventHub.$on('confirmUnreviewTemplate', confirmUnreviewTemplate)
  window.eventHub.$on('reloadWorkflowReviewStatusData', reloadData)
})

onBeforeUnmount(() => {
  window.eventHub.$off('confirmUnreviewTemplate', confirmUnreviewTemplate)
  window.eventHub.$off('reloadWorkflowReviewStatusData', reloadData)
})

function handleItemReviewUpdate(
  {
    review,
    reviews,
  }: { review: TemplateReview | null; reviews: Props['reviews'] } = {
    review: null,
    reviews: [],
  }
) {
  effectiveReviews.value = reviews
  effectiveReview.value = review
}

function confirmUnreviewTemplate({
  callback,
  keepOpenAfterConfirmation,
}: {
  callback: () => void
  keepOpenAfterConfirmation?: boolean
}) {
  if (hasAnyUpToDateReview.value) {
    window.modalCitizen.openModal(
      'confirm-unreview-template-modal',
      {
        onConfirm: () => {
          callback()
        },
        reviewers: reviewers.value,
        keepOpenAfterConfirmation,
      },
      {
        autoClose: false,
      }
    )
  } else {
    callback()
  }
}

async function reloadData() {
  // This functionality is not used when the itemClass is undefined.
  if (!props.itemClass) return
  // Reloading data is currently only used to change the review status
  // from reviewed to unreviewed. It makes no sense to reload the data when
  // the template is already unreviewed.
  if (!hasAnyUpToDateReview.value) return

  const url = window.Silverfin.Routes.index_ledger_workflow_reviews_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    props.ledgerId,
    props.itemClass,
    props.itemId
  )

  try {
    const data = (await SFetch.jsonWithAlert(url)) as {
      review: TemplateReview | null
      reviews: Props['reviews']
    }
    const { review, reviews } = data
    handleItemReviewUpdate({ review, reviews })
  } catch (e) {
    SfLog.warn('Error during reloading the workflow review status data', {
      extra: {
        error: e,
      },
    })
  }
}
</script>
